import { useMemo } from 'react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { DEFAULT_PERMISSIONS, type Permissions, type UserRole } from '@/types/clm';

export function usePermissions() {
  const { currentMember } = useOrganization();

  const permissions: Permissions = useMemo(() => {
    if (!currentMember) {
      // No member = viewer permissions (most restrictive)
      return DEFAULT_PERMISSIONS.viewer;
    }

    // Get default permissions for role
    const defaultPerms = DEFAULT_PERMISSIONS[currentMember.role as UserRole] || DEFAULT_PERMISSIONS.viewer;

    // Merge with custom permissions (if any)
    const customPerms = currentMember.permissions || {};

    return {
      ...defaultPerms,
      ...customPerms,
    };
  }, [currentMember]);

  // Helper functions for common checks
  const can = (permission: keyof Permissions): boolean => {
    return permissions[permission] === true;
  };

  const isRole = (...roles: UserRole[]): boolean => {
    if (!currentMember) return false;
    return roles.includes(currentMember.role as UserRole);
  };

  const isAdmin = currentMember?.role === 'admin';
  const isLegal = currentMember?.role === 'legal';
  const isRequester = currentMember?.role === 'requester';
  const isApprover = currentMember?.role === 'approver';
  const isViewer = currentMember?.role === 'viewer';

  return {
    permissions,
    can,
    isRole,
    isAdmin,
    isLegal,
    isRequester,
    isApprover,
    isViewer,
    role: currentMember?.role || 'viewer',
  };
}
